import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Alert,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import dayjs from 'dayjs';
import Loading from '../../../../components/loading';
import { useParams } from 'react-router-dom';
import { useProfessionals } from '../../../../hooks/useProfessionals';
import { useSaveAttested } from '../hooks/useSaveAttested';
import { useCidCatalog } from '../../../../hooks/useCidCatalog';
import CidFormModal from '../../../../register/cidCatalog/cidFormModal';

const attestedTypes = [
  {
    value: 'COMPARECIMENTO',
    label: 'Atestado de Comparecimento na Consulta',
  },
  {
    value: 'REPOUSO',
    label: 'Atestado de Repouso',
  },
];

export default function AttestedModalMobile({ open, onClose, onAttestedSave }) {
  const { patientId } = useParams();

  const [isLoading, setLoading] = useState(false);

  const { professionals: comboBoxDentists } = useProfessionals();
  const { save: saveAttestedRequest } = useSaveAttested();
  const { cids: comboBoxCids, refetch: refetchCids } = useCidCatalog();
  const [isCidFormOpen, setCidFormOpen] = useState(false);
  const [cidFormInitialDescription, setCidFormInitialDescription] = useState('');
  const [selectedDentistId, setSelectedDentistId] = useState('');

  const [selectedAttestedType, setSelectedAttestedType] = useState('');
  const [date, setDate] = useState(dayjs());
  const [daysOfRest, setDaysOfRest] = useState('');
  const [selectedCid, setSelectedCid] = useState(null);
  const [cidInputValue, setCidInputValue] = useState('');
  const [error, setError] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const saveAttested = () => {
    setError('');
    if (!selectedDentistId || !selectedAttestedType || !date) {
      setError('É obrigatório selecionar: Profissional, Tipo de Atestado e data');
      return;
    }
    if (
      selectedAttestedType === 'REPOUSO' &&
      (!daysOfRest || parseInt(daysOfRest) <= 0 || !selectedDentistId)
    ) {
      setError('É obrigatório selecionar: Profissional, Tipo de Atestado e data');
      return;
    }

    if (
      selectedAttestedType === 'COMPARECIMENTO' &&
      (!startTime || !endTime || !selectedDentistId)
    ) {
      setError('É obrigatório selecionar: Profissional, Tipo de Atestado e data');
      return;
    }

    const attested = {
      dentistaId: selectedDentistId,
      pacienteId: patientId,
      tipoDeAtestado: selectedAttestedType,
      cidId: selectedCid?.id ?? null,
      quantidadeDeDiasDeRepouso: parseInt(daysOfRest) || 0,
      horaInicial: dayjs(startTime).format('HH:mm'),
      horaFinal: dayjs(endTime).format('HH:mm'),
      data: date ? dayjs(date).format('DD/MM/YYYY') : null,
    };

    setLoading(true);
    saveAttestedRequest(attested)
      .then((returnFromApi) => {
        onAttestedSave(returnFromApi);
        cleanFields();
        setLoading(false);
      })
      .catch((returnFromApi) => {
        onAttestedSave(returnFromApi);
        console.error('Erro ao salvar atestado:', returnFromApi);
        setLoading(false);
      });
  };

  function cleanFields() {
    setSelectedDentistId('');
    setSelectedAttestedType('');
    setDate(dayjs());
    setDaysOfRest('');
    setSelectedCid(null);
    setCidInputValue('');
    setCidFormInitialDescription('');
    setStartTime(null);
    setEndTime(null);
    setError('');
  }

  const openCidForm = () => {
    setCidFormInitialDescription(cidInputValue);
    setCidFormOpen(true);
  };
  const closeCidForm = () => setCidFormOpen(false);

  // Depois de cadastrar um CID pelo atalho, recarrega o catálogo e já deixa o novo selecionado.
  const handleCidCreated = (savedCid) => {
    refetchCids().then((freshCids) => {
      const created =
        savedCid?.id != null
          ? savedCid
          : (freshCids ?? []).find((item) => item.code === savedCid?.code);
      if (created?.id != null) {
        setSelectedCid(created);
        setCidInputValue(`${created.code} - ${created.description}`);
      }
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <Loading isToHidden={isLoading === false} />
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Atestado
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Collapse in={!!error} sx={{ mt: 1, mb: 1 }}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Collapse>

          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="dentist-select-label">Profissional</InputLabel>
              <Select
                labelId="dentist-combobox-label"
                value={selectedDentistId}
                label="Profissional"
                onChange={(e) => setSelectedDentistId(e.target.value)}
              >
                {comboBoxDentists.length === 0 && (
                  <MenuItem disabled value="">
                    Nenhum profissional encontrado
                  </MenuItem>
                )}
                {comboBoxDentists.map((dentist) => (
                  <MenuItem key={dentist.id} value={dentist.id}>
                    {dentist.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="cert-type-label">Tipo de Atestado</InputLabel>
              <Select
                value={selectedAttestedType}
                label="Tipo de Atestado"
                onChange={(e) => setSelectedAttestedType(e.target.value)}
              >
                {attestedTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DatePicker
              label="Data"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              format="DD/MM/YYYY"
            />

            {selectedAttestedType === 'REPOUSO' && (
              <TextField
                fullWidth
                label="Dias de repouso"
                type="number"
                value={daysOfRest}
                onChange={(e) => setDaysOfRest(e.target.value)}
              />
            )}

            {selectedAttestedType === 'COMPARECIMENTO' && (
              <>
                <TimeField
                  label="Hora Inicial"
                  value={startTime}
                  onChange={(newValue) => setStartTime(newValue)}
                  format="HH:mm"
                />
                <TimeField
                  label="Hora Final"
                  value={endTime}
                  onChange={(newValue) => setEndTime(newValue)}
                  format="HH:mm"
                />
              </>
            )}

            <FormControl fullWidth>
              <Autocomplete
                id="cid-combobox"
                options={comboBoxCids}
                getOptionLabel={(option) => `${option.code} - ${option.description}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedCid}
                inputValue={cidInputValue}
                onInputChange={(_, newInputValue) => setCidInputValue(newInputValue)}
                onChange={(_, newValue) => setSelectedCid(newValue)}
                noOptionsText="Nenhum CID encontrado."
                PaperComponent={({ children, ...other }) => (
                  <Paper {...other}>
                    {children}
                    <Box
                      sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Button fullWidth size="small" onClick={openCidForm}>
                        + Adicionar Novo CID
                      </Button>
                    </Box>
                  </Paper>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="CID" fullWidth variant="outlined" />
                )}
              />
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={saveAttested} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <CidFormModal
        open={isCidFormOpen}
        onClose={closeCidForm}
        onSave={handleCidCreated}
        initialDescription={cidFormInitialDescription}
      />
    </>
  );
}
